import { NodeType, type NodeTypeEnum } from "./workflow.types";

/**
 * Node types whose `node.data.runOutput` is real persisted state rather than a
 * test artefact, so it survives every write. Each entry names a reader:
 *
 *  - `agentInputTrigger` — `runOutput` holds the agent's test PARAMETERS, not a
 *    test result. `V1WorkflowTestingService.testWorkflow` writes them there and
 *    `WorkflowExecutionService.startTestWorkflow` seeds the run from them.
 *  - `humanInput` — read at EXECUTION time by `HumanInputNode.execute`, as the
 *    node's output when the execution carries no trigger message.
 *  - `pluginTrigger` — the seed a downstream per-node test resolves the trigger
 *    from when the node carries no `exampleData`: `resolveTriggerTestOutput`
 *    (`apps/backend/src/workflow/utils/utils.ts`) reads it as the fallback for
 *    every `EXAMPLE_DATA_FIRST_TRIGGERS` type. A Pipedream trigger cannot be
 *    fired on demand, so a stored seed is the only way to exercise the nodes
 *    downstream of one before publish.
 *  - `newsMonitorTrigger` — a real monitor payload captured from a node test.
 *    The editor's Test tab writes the received event here
 *    (`news-monitor-trigger-panel-content.tsx`) and renders it back from here,
 *    and `V1WorkflowTestingService.testWorkflow` prefers the persisted copy over
 *    a synthesized sample so genuine monitor data is not discarded.
 *
 * ## Why this lives in `@nexus/types` and not beside any one reader
 *
 * FOUR call sites answer FOUR different questions about the same set, on both
 * sides of the wire, and every one of them used to carry its own copy:
 *
 * | call site | question |
 * |---|---|
 * | `cleanFlowElements.cleanNodes` (frontend) | what the editor SENDS on a draft save, and what counts as a change |
 * | `sanitize-workflow-for-api` (frontend) | what the editor SENDS on a node/workflow test |
 * | `stripTransientFields` (backend) | what the server STORES, whichever client sent it |
 * | `WorkflowExecutionService.startTestWorkflow` (backend) | what survives the IN-FLIGHT test payload |
 *
 * The questions stay separate — merging them is what produced the disagreement
 * in the first place — but the SET of node types is one fact, so it is declared
 * once, here, where both `apps/frontend` and `apps/backend` can import it.
 * `apps/backend/src/**` is not reachable from the frontend, which is why a
 * backend-side constant could never have been the single definition.
 *
 * Adding an entry here is therefore the whole change: every call site follows.
 */
export const RUN_OUTPUT_PERSISTED_NODE_TYPES: readonly NodeTypeEnum[] = [
  NodeType.agentInputTrigger,
  NodeType.humanInput,
  NodeType.pluginTrigger,
  NodeType.newsMonitorTrigger
];

/**
 * Whether a node type's `runOutput` is persisted state rather than a test
 * artefact.
 *
 * Takes a plain `string` on purpose: every caller reads `node.type` off a React
 * Flow node or a raw JSON graph, where the type is `string | undefined` and no
 * cast should be needed to ask this question.
 */
export function keepsRunOutput(nodeType: string | undefined): boolean {
  return (RUN_OUTPUT_PERSISTED_NODE_TYPES as readonly string[]).includes(nodeType ?? "");
}
